import {
    FetchState,
    UserIdpProfileState,
    UserAccountCredentialsResponse,
    IdpStatusChangeRequest,
    IdpEmailChangeRequest
} from "@/shared/types";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: UserIdpProfileState = {
    fetchState: FetchState.Uninitialized,
    username: null,
    userProfile: null
};

export const useUserIdpInformationStore = defineStore("userIdpInformation", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        isProfileLoading: state => state.fetchState === FetchState.Loading,
        isProfileLoaded: state => state.fetchState === FetchState.Loaded,
        isProfileFailed: state => state.fetchState === FetchState.Failed
    },
    actions: {
        setFetchState (
            commitState: { fetchState: FetchState, username: string }): void {
            this.fetchState = commitState.fetchState;
            this.username = commitState.username;
        },
        setProfile (commitState: { key: string, data: UserAccountCredentialsResponse }) {
            if (this.username === commitState.key) {
                this.userProfile = commitState.data;
            }
        },
        getProfile ({ partnerId, username, force = false }) {
            if (this.username === username && !force) return;
            this.setFetchState({ fetchState: FetchState.Loading, username });
            return UserService.idpInfoProfile(partnerId, username)
                .then(response => {
                    this.setProfile({ key: username, data: response.data });
                    this.setFetchState({ fetchState: FetchState.Loaded, username });
                }).catch(err => {
                    this.setFetchState({ fetchState: FetchState.Failed, username });
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        changeStatus (statusChange: IdpStatusChangeRequest) {
            if (this.username !== statusChange.username) return;
            const username = statusChange.username;
            this.setFetchState({ fetchState: FetchState.Loading, username });
            return UserService.idpInfoChangeStatus(statusChange)
                .then(response => {
                    this.setProfile({ key: username, data: response.data });
                    this.setFetchState({ fetchState: FetchState.Loaded, username });
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        changeSuspendStatus (statusChange: IdpStatusChangeRequest) {
            if (this.username !== statusChange.username) return;
            const username = statusChange.username;
            this.setFetchState({ fetchState: FetchState.Loading, username });
            return UserService.idpInfoChangeSuspendStatus(statusChange)
                .then(response => {
                    this.setProfile({ key: username, data: response.data });
                    this.setFetchState({ fetchState: FetchState.Loaded, username });
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        sendIdpEmailVerificationMessage (idpEmailChangeRequest: IdpEmailChangeRequest) {
            if (this.username !== idpEmailChangeRequest.username) return;
            const username = idpEmailChangeRequest.username;
            this.setFetchState({ fetchState: FetchState.Loading, username });
            return UserService.sendIdpEmailVerificationMessage(idpEmailChangeRequest)
                .then(response => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Verification email sent.");
                    this.setProfile({ key: username, data: response.data });
                    this.setFetchState({ fetchState: FetchState.Loaded, username });
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        resetIdpAccount (args: { username: string, partnerId: number }) {
            if (this.username !== args.username) return;
            this.setFetchState({ fetchState: FetchState.Loading, username: args.username });
            return UserService.resetUserIdpAccount(args.username, args.partnerId)
                .then(response => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Account reset successfully.");
                    this.setProfile({ key: args.username, data: response.data });
                })
                .catch(error => {
                    storeHelper.handleError({ error, rethrow: true });
                })
                .finally(() => {
                    this.setFetchState({ fetchState: FetchState.Loaded, username: args.username });
                });
        },
        enableIdpAccount (args: { username: string, partnerId: number }) {
            if (this.username !== args.username) return;
            this.setFetchState({ fetchState: FetchState.Loading, username: args.username });
            return UserService.enableUserIdpAccount(args.username, args.partnerId)
                .then(response => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Account enabled successfully.");
                    this.setProfile({ key: args.username, data: response.data });
                })
                .catch(error => {
                    storeHelper.handleError({ error, rethrow: true });
                })
                .finally(() => {
                    this.setFetchState({ fetchState: FetchState.Loaded, username: args.username });
                });
        },
        syncIdpAccount (args: { username: string, partnerId: number }) {
            if (this.username !== args.username) return;
            this.setFetchState({ fetchState: FetchState.Loading, username: args.username });
            return UserService.syncUserIdpAccount(args.username, args.partnerId)
                .then(() => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Account synced successfully.");
                })
                .catch(error => {
                    storeHelper.handleError({ error, rethrow: true });
                })
                .finally(() => {
                    this.setFetchState({ fetchState: FetchState.Loaded, username: args.username });
                });
        }
    }
});
